/**
 * React Query hooks for FOS Explorer
 *
 * Provides data fetching and caching for:
 * - Semantic search across FOS decisions
 * - Faceted filtering
 * - Decision details
 * - Corpus statistics
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  searchFOSDecisions,
  getFOSFacets,
  getFOSStats,
  getFOSDecisionById,
  getSimilarFOSDecisions,
  getFOSProjections,
  getFOSSignalProjections,
  FOSSearchParams,
  FOSSearchResult,
  FOSFacets,
  FOSCorpusStats,
  FOSDecisionDetail,
  FOSDecisionCard,
  FOSProjectionData,
  FOSSignalProjectionsResponse,
} from '../utils/api';

// Query keys for cache management
export const fosQueryKeys = {
  all: ['fos'] as const,
  search: (params: FOSSearchParams) => [...fosQueryKeys.all, 'search', params] as const,
  facets: () => [...fosQueryKeys.all, 'facets'] as const,
  stats: () => [...fosQueryKeys.all, 'stats'] as const,
  decision: (id: string) => [...fosQueryKeys.all, 'decision', id] as const,
  similar: (signalId: string) => [...fosQueryKeys.all, 'similar', signalId] as const,
  projections: () => [...fosQueryKeys.all, 'projections'] as const,
  signalProjections: (days: number) => [...fosQueryKeys.all, 'signalProjections', days] as const,
};

/**
 * Hook for searching FOS decisions with semantic search
 *
 * @param params - Search parameters including query and filters
 * @param options - Additional React Query options
 */
export function useFOSSearch(
  params: FOSSearchParams | null,
  options?: {
    enabled?: boolean;
    keepPreviousData?: boolean;
  }
) {
  return useQuery<FOSSearchResult, Error>({
    queryKey: fosQueryKeys.search(params || { query: '' }),
    queryFn: () => searchFOSDecisions(params!),
    enabled: options?.enabled !== false && !!params?.query,
    placeholderData: options?.keepPreviousData ? (prev) => prev : undefined,
    staleTime: 60 * 1000, // 1 minute
    gcTime: 5 * 60 * 1000, // 5 minutes
  });
}

/**
 * Hook for searching FOS decisions with debouncing
 * Use this for search-as-you-type experiences
 */
export function useFOSSearchMutation() {
  return useMutation<FOSSearchResult, Error, FOSSearchParams>({
    mutationFn: searchFOSDecisions,
  });
}

/**
 * Hook for getting FOS facets (filter options)
 */
export function useFOSFacets() {
  return useQuery<FOSFacets, Error>({
    queryKey: fosQueryKeys.facets(),
    queryFn: getFOSFacets,
    staleTime: 5 * 60 * 1000, // 5 minutes - facets don't change often
    gcTime: 30 * 60 * 1000, // 30 minutes
  });
}

/**
 * Hook for getting FOS corpus statistics
 */
export function useFOSStats() {
  return useQuery<FOSCorpusStats, Error>({
    queryKey: fosQueryKeys.stats(),
    queryFn: getFOSStats,
    staleTime: 60 * 1000, // 1 minute
    gcTime: 5 * 60 * 1000, // 5 minutes
  });
}

/**
 * Hook for getting a single FOS decision detail
 *
 * @param decisionId - The decision UUID
 */
export function useFOSDecision(decisionId: string | null) {
  return useQuery<FOSDecisionDetail, Error>({
    queryKey: fosQueryKeys.decision(decisionId || ''),
    queryFn: () => getFOSDecisionById(decisionId!),
    enabled: !!decisionId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 30 * 60 * 1000, // 30 minutes
  });
}

/**
 * Hook for getting similar FOS decisions for a signal
 *
 * @param signalId - The signal UUID
 * @param limit - Maximum number of results
 * @param threshold - Minimum similarity threshold
 */
export function useSimilarFOSDecisions(
  signalId: string | null,
  limit: number = 5,
  threshold: number = 0.5
) {
  return useQuery<FOSDecisionCard[], Error>({
    queryKey: fosQueryKeys.similar(signalId || ''),
    queryFn: () => getSimilarFOSDecisions(signalId!, limit, threshold),
    enabled: !!signalId,
    staleTime: 60 * 1000, // 1 minute
    gcTime: 5 * 60 * 1000, // 5 minutes
  });
}

/**
 * Hook for prefetching FOS data (call on hover/focus for faster navigation)
 */
export function useFOSPrefetch() {
  const queryClient = useQueryClient();

  const prefetchDecision = (decisionId: string) => {
    queryClient.prefetchQuery({
      queryKey: fosQueryKeys.decision(decisionId),
      queryFn: () => getFOSDecisionById(decisionId),
      staleTime: 5 * 60 * 1000,
    });
  };

  const prefetchFacets = () => {
    queryClient.prefetchQuery({
      queryKey: fosQueryKeys.facets(),
      queryFn: getFOSFacets,
      staleTime: 5 * 60 * 1000,
    });
  };

  return { prefetchDecision, prefetchFacets };
}

/**
 * Hook for getting FOS projection data for visualization
 *
 * Returns UMAP 2D coordinates and cluster assignments for all embedded decisions.
 * Data is cached for 10 minutes as it changes rarely.
 */
export function useFOSProjections(options?: { enabled?: boolean }) {
  return useQuery<FOSProjectionData, Error>({
    queryKey: fosQueryKeys.projections(),
    queryFn: () => getFOSProjections(true),
    enabled: options?.enabled !== false,
    staleTime: 10 * 60 * 1000, // 10 minutes - projections rarely change
    gcTime: 60 * 60 * 1000, // 1 hour
    retry: 1, // Only retry once - 404 means projections not computed yet
  });
}

/**
 * Hook for getting organization signal projections overlaid on FOS space
 *
 * Projects recent signals into the same UMAP space as FOS decisions
 * for comparison visualization.
 *
 * @param days - Number of days to look back for signals (default 30)
 * @param options - Additional React Query options
 */
export function useFOSSignalProjections(
  days: number = 30,
  options?: { enabled?: boolean }
) {
  return useQuery<FOSSignalProjectionsResponse, Error>({
    queryKey: fosQueryKeys.signalProjections(days),
    queryFn: () => getFOSSignalProjections(days),
    enabled: options?.enabled !== false,
    staleTime: 5 * 60 * 1000, // 5 minutes - signals may be added frequently
    gcTime: 30 * 60 * 1000, // 30 minutes
    retry: 1, // Only retry once - 404 means base projections not computed
  });
}
